import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { variantKey } from "../data/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StockContext exposes the LIVE inventory map fetched from the backend.
 *
 * Shape (`stockMap`):
 *   {
 *     "033": { qty: 0, sizes: { XXS: 1 }, has_sizes: true },
 *     "061": { qty: 4, sizes: {}, has_sizes: false },
 *     ...
 *   }
 *
 * Helpers:
 *   - productTotalQty(id)                  -> total remaining units
 *   - sizeQty(id, sizeLabel)               -> remaining units for that size
 *   - isProductOutOfStock(id)              -> true when nothing is buyable
 *   - isSizeOutOfStock(id, sizeLabel)      -> true when the chosen size is sold out
 *   - refreshStock()                       -> re-fetch from API (used after orders)
 */
const StockContext = createContext(null);

export function StockProvider({ children }) {
  const [stockMap, setStockMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  const fetchStock = useCallback(async () => {
    try {
      const res = await fetch(`${API}/products/stock`);
      if (!res.ok) throw new Error(`Stock API ${res.status}`);
      const data = await res.json();
      const next = {};
      for (const it of data.items || []) {
        next[it.product_id] = {
          qty: Number(it.qty || 0),
          sizes: it.sizes || {},
          has_sizes: !!it.has_sizes,
        };
      }
      setStockMap(next);
      setLoaded(true);
      return next;
    } catch (err) {
      console.warn("[Stock] fetchStock failed:", err);
      setLoaded(true);
      return null;
    }
  }, []);

  // Fetch live values only. Do not POST init-stock from the public
  // storefront — that seed used catalog qty 1 and put sold-out items back.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await fetchStock();
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchStock]);

  const value = useMemo(() => {
    const productEntry = (id) => stockMap[id];

    const productTotalQty = (id) => {
      const e = productEntry(id);
      if (!e) return null; // unknown — treat as available until first fetch
      if (e.has_sizes) {
        return Object.values(e.sizes || {}).reduce((a, b) => a + Number(b || 0), 0);
      }
      return Number(e.qty || 0);
    };

    const sizeQty = (id, sizeLabel) => {
      const e = productEntry(id);
      if (!e) return null;
      if (!e.has_sizes) return Number(e.qty || 0);
      return Number((e.sizes || {})[sizeLabel] || 0);
    };

    // Available qty for a specific (size, color) variant. Falls back to plain
    // `sizeQty` when no color is supplied so legacy size-only products keep
    // working unchanged.
    const variantQty = (id, sizeLabel, colorEn) => {
      const e = productEntry(id);
      if (!e) return null;
      if (!e.has_sizes) return Number(e.qty || 0);
      const key = variantKey(sizeLabel, colorEn);
      return Number((e.sizes || {})[key] || 0);
    };

    // True when every variant of a given color is sold out — used to grey out
    // color swatches when the entire color line is unavailable.
    const isColorOutOfStock = (id, colorEn) => {
      const e = productEntry(id);
      if (!e) return false;
      if (!e.has_sizes) return Number(e.qty || 0) <= 0;
      const sizes = e.sizes || {};
      // Sum qty across all variant keys that end with "::colorEn" OR equal colorEn
      const suffix = `::${colorEn}`;
      let total = 0;
      for (const [k, v] of Object.entries(sizes)) {
        if (k === colorEn || k.endsWith(suffix)) total += Number(v || 0);
      }
      return total <= 0;
    };

    const isProductOutOfStock = (id) => {
      if (!loaded) return false;
      const total = productTotalQty(id);
      if (total === null) return true;
      return total <= 0;
    };

    const isSizeOutOfStock = (id, sizeLabel) => {
      if (!loaded) return false;
      const q = sizeQty(id, sizeLabel);
      if (q === null) return true;
      return q <= 0;
    };

    return {
      stockMap,
      loaded,
      productTotalQty,
      sizeQty,
      variantQty,
      isColorOutOfStock,
      isProductOutOfStock,
      isSizeOutOfStock,
      refreshStock: fetchStock,
    };
  }, [stockMap, loaded, fetchStock]);

  return <StockContext.Provider value={value}>{children}</StockContext.Provider>;
}

export const useStock = () => {
  const ctx = useContext(StockContext);
  if (!ctx) throw new Error("useStock must be used within a StockProvider");
  return ctx;
};
